import * as Yup from 'yup'
import { memo } from 'react'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'

import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'

import logger from '../helpers/logger'
import { ICredentials } from '../types/'

import FormButton from './ui/FormButton'
import LoginSettingsPopover from './LoginSettingsPopover'
import ControlledTextField from './ui/ControlledTextField'

type IFormInputs = ICredentials

function LoginForm({ onSubmit }: { onSubmit: (data: IFormInputs) => Promise<void | string> }): JSX.Element {
  const { t } = useTranslation()

  const validationSchema: Yup.SchemaOf<IFormInputs> = Yup.object().shape({
    userName: Yup.string().defined().required(t('requiredField')),
    password: Yup.string().defined().required(t('requiredField')).min(8, t('minEightChars')),
  })
  const { control, handleSubmit } = useForm<IFormInputs>({
    defaultValues: { userName: '', password: '' },
    resolver: yupResolver(validationSchema),
  })

  const onFormSubmit = handleSubmit(async (values: IFormInputs): Promise<void> => {
    try {
      await onSubmit(values)
    } catch (err) {
      logger.log(err)
    }
  })

  return (
    <form onSubmit={onFormSubmit}>
      <Grid container sx={{ flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
        <Grid container item spacing={4} sm={7} sx={{ py: 5, px: 4 }}>
          <Grid item>
            <Typography variant="h5" component="span" gutterBottom>
              {t(`logInTitle`)}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <ControlledTextField fullWidth name="userName" control={control} label={`${t('userName')}`} />
          </Grid>

          <Grid item xs={12}>
            <ControlledTextField
              fullWidth
              name="password"
              type="password"
              control={control}
              label={`${t('password')}`}
            />
          </Grid>

          <Grid container item justifyContent="space-between" alignItems="center">
            <Grid item>
              <FormButton type="submit" control={control}>
                {t('signIn')}
              </FormButton>
            </Grid>

            <LoginSettingsPopover />
          </Grid>
        </Grid>

        <Grid
          item
          container
          alignItems="center"
          justifyContent="center"
          sm={5}
          sx={{ borderLeft: { sm: 1 }, borderLeftColor: { sm: 'lightgray' }, pt: { sm: 0, xs: 6 } }}
        >
          <img alt="Captello" src="/images/logo-color-leadliaison.png" style={{ width: 175 }} />
        </Grid>
      </Grid>
    </form>
  )
}

export default memo(LoginForm)
